/**
 * Site-wide "download the app" smart banner, fixed to the bottom of the
 * viewport on mobile browsers only.
 *
 * Installed-app detection: on Android Chrome we ask
 * `navigator.getInstalledRelatedApps()` and hide the banner when the Meiuca
 * app is already installed (the check only yields results once the Android
 * app declares this site in its `asset_statements`; until then it returns an
 * empty list and the banner shows). iOS has no installed-app API, so there
 * the banner always shows until dismissed. Dismissal is remembered in
 * `localStorage` for a cool-down period.
 *
 * Hidden on routes that either already carry their own download CTA
 * (`/pay/:token`) or are rendered inside the native apps' webviews / popups
 * (legal pages, auth connect, internal tools).
 */

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { Body, Caption } from '@meiuca/ui-components'
import { X } from 'lucide-react'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents'
import { APP_STORE_URL, PLAY_STORE_URL } from '@/constants/appStoreLinks'

const DISMISSED_AT_KEY = 'meiuca.appBanner.dismissedAt'
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000 // 14 days

const HIDDEN_PATH_PREFIXES = [
  '/pay/',
  '/auth/connect',
  '/tools/',
  '/termos',
  '/privacidade',
  '/excluir-conta',
  '/arenas/termos',
  '/arenas/privacidade',
  '/centros-de-treinamento/termos',
  '/centros-de-treinamento/privacidade',
]

type MobilePlatform = 'ios' | 'android'

function detectMobilePlatform(): MobilePlatform | null {
  const ua = navigator.userAgent
  if (/android/i.test(ua)) return 'android'
  // iPadOS 13+ reports a macOS user agent; the touch check catches it.
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  if (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return 'ios'
  return null
}

function wasRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISSED_AT_KEY)
    if (!raw) return false
    const dismissedAt = Number(raw)
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS
  } catch {
    return false
  }
}

async function isNativeAppInstalled(): Promise<boolean> {
  // Non-standard API, currently Android Chrome only.
  const nav = navigator as Navigator & {
    getInstalledRelatedApps?: () => Promise<Array<{ id?: string }>>
  }
  if (typeof nav.getInstalledRelatedApps !== 'function') return false
  try {
    const apps = await nav.getInstalledRelatedApps()
    return apps.length > 0
  } catch {
    return false
  }
}

export function SmartAppBanner() {
  const { pathname } = useLocation()
  const [platform, setPlatform] = useState<MobilePlatform | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const detected = detectMobilePlatform()
    setPlatform(detected)
    if (!detected || wasRecentlyDismissed()) return

    let cancelled = false
    isNativeAppInstalled().then((installed) => {
      if (!cancelled && !installed) setVisible(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const hiddenOnRoute = HIDDEN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  if (!visible || !platform || hiddenOnRoute) return null

  const store = platform === 'ios' ? 'app_store' : 'google_play'
  const storeUrl = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL

  const dismiss = () => {
    setVisible(false)
    try {
      localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()))
    } catch {
      // Storage unavailable (private mode) — banner just reappears next visit.
    }
    analyticsService.track(ANALYTICS_EVENTS.DOWNLOAD_APP_BANNER_DISMISSED, { store })
  }

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:hidden">
      <div className="mx-auto max-w-md flex items-center gap-3 rounded-2xl bg-background-white shadow-lg border border-border-light p-3">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fechar"
          className="flex-shrink-0 text-text-tertiary hover:text-text-secondary transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <img src="/favicon.svg" alt="" className="w-10 h-10 rounded-xl" width={40} height={40} />
        <div className="flex-1 min-w-0">
          <Body className="text-text-primary text-sm font-semibold">Meiuca</Body>
          <Caption className="text-text-secondary text-xs truncate">
            Reservas, treinos e pagamentos no app
          </Caption>
        </div>
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            analyticsService.track(ANALYTICS_EVENTS.DOWNLOAD_APP_BANNER_CLICKED, { store })
          }
          className="flex-shrink-0 rounded-full bg-brand-primary text-white text-sm font-semibold px-4 py-2 hover:opacity-90 transition-opacity"
        >
          Baixar
        </a>
      </div>
    </div>
  )
}
